/**
 * `/signin/verify?token=…` — where the link in the email lands.
 *
 * ── Why the token comes through an SPA route at all ─────────────────────────
 *
 * The link could have pointed straight at the API and set a cookie in one hop.
 * It does not, because the token is single-use and mail is full of things that
 * fetch URLs without a human: scanners, link-preview bots, corporate security
 * appliances that "detonate" every link in inbound mail. Any one of them would
 * spend the customer's token before they clicked it, and the customer would see
 * "already used" on a link they had not touched.
 *
 * Landing on an HTML page that then POSTs means the token survives everything
 * that merely fetches the URL.
 *
 * ── Two things this screen does that are easy to leave out ──────────────────
 *
 *  1. It strips the token from the address bar the moment it has been spent, so
 *     the customer's history, their `Referer` on the next outbound click, and
 *     any screenshot of the page do not carry a credential — spent or not.
 *  2. It runs exactly once. React 18+ mounts effects twice in development, and
 *     a verify screen that fires twice tells its user their brand-new link was
 *     already used. The guard is a ref rather than state because it must hold
 *     across the remount, not across a render.
 *
 * ── Why the result is never discarded ───────────────────────────────────────
 *
 * The effect below deliberately has no "cancelled" flag, and that is the whole
 * correctness argument for this screen. A run-once guard and a cancel-on-
 * teardown flag cannot both be right: the teardown suppresses the in-flight
 * result, and the guard then refuses to ask again, so the reply from a request
 * that DID sign the customer in is dropped and the screen waits forever on a
 * token that has already been spent. The teardown fires for reasons that have
 * nothing to do with this request — the StrictMode remount, and any change to
 * the session context, which `/api/session` answering makes certain — so this
 * was not an edge case; it was the common path.
 *
 * By the time the promise resolves the session exists on the server and the
 * cookie is set. The only honest thing to do with that answer is publish it.
 * `onScreen` gates the navigation alone, so somebody who clicked away while the
 * request was in flight is not yanked back, and it is re-armed at the top of
 * every run so the StrictMode remount does not read as a departure.
 */

import { PageHeader } from "@/components/PageHeader";
import { Notice, Strip, StripBody } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { PortalError, portalApi } from "@/lib/api";
import { useSession } from "@/lib/session";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

type Phase = { kind: "working" } | { kind: "failed"; code: string; message: string };

export function VerifyRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setState } = useSession();
  const [phase, setPhase] = useState<Phase>({ kind: "working" });

  const token = params.get("token");
  const next = params.get("next");
  const started = useRef(false);
  const onScreen = useRef(true);

  useEffect(() => {
    // Re-armed before the guard, so the StrictMode remount — cleanup, then this
    // run again — leaves it true rather than stranding the redirect.
    onScreen.current = true;

    if (!started.current) {
      started.current = true;

      if (!token) {
        setPhase({
          kind: "failed",
          code: "link_invalid",
          message:
            "This link is missing its token. Mail clients sometimes cut long URLs in half — try pasting the whole thing, or ask for a new link.",
        });
      } else {
        portalApi
          .verifyLink(token)
          .then((state) => {
            // Unconditional: the session is real by now, and a screen that
            // quietly drops it leaves a signed-in customer looking at a page
            // that says otherwise.
            setState(state);
            if (!onScreen.current) return;
            // `replace`, so Back does not return to a URL holding a spent token.
            navigate(next ?? state.next ?? "/", { replace: true });
          })
          .catch((err: unknown) => {
            const failure =
              err instanceof PortalError
                ? { code: err.code, message: err.message }
                : {
                    code: "internal",
                    message:
                      "We could not check that link. Your connection may have dropped — try it again.",
                  };
            setPhase({ kind: "failed", ...failure });
          });
      }
    }

    return () => {
      onScreen.current = false;
    };
  }, [token, next, navigate, setState]);

  // Strip the credential out of the address bar immediately, before the request
  // even resolves. There is nothing on this screen that needs to read it again
  // — the effect above already holds it — and leaving it in the URL puts it in
  // history and in every screenshot of a failure someone sends to support.
  useEffect(() => {
    if (!token) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("token")) return;
    url.searchParams.delete("token");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, [token]);

  if (phase.kind === "working") {
    return (
      <div className="mx-auto max-w-xl">
        <PageHeader code="ACC" label="Sign in" title="Signing you in…" />
        <Strip>
          <StripBody>
            <div className="load-track h-1 w-full rounded-[2px]" aria-hidden="true" />
            <p className="mt-4 text-[0.9375rem] leading-relaxed text-ink-2">
              One moment — checking your link.
            </p>
          </StripBody>
        </Strip>
      </div>
    );
  }

  return <VerifyFailed code={phase.code} message={phase.message} next={next} />;
}

/**
 * The three ordinary failures lead to three different actions, so they get
 * three different headings. The heading is what somebody reads before deciding
 * whether they have done something wrong — "expired" and "already used" are
 * both fine, and neither should read like an error the customer caused.
 */
const HEADINGS: Readonly<Record<string, string>> = {
  link_expired: "That link has expired",
  link_used: "That link has already been used",
  account_blocked: "This account cannot sign in",
  link_invalid: "We do not recognise that link",
};

function VerifyFailed({
  code,
  message,
  next,
}: {
  code: string;
  message: string;
  next: string | null;
}) {
  const blocked = code === "account_blocked";
  const signInTo = next ? `/signin?next=${encodeURIComponent(next)}` : "/signin";

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader code="ACC" label="Sign in" title={HEADINGS[code] ?? "We could not sign you in"} />

      <Notice tone={blocked ? "hold" : "marker"} label={blocked ? "Blocked" : "Try again"}>
        {message}
      </Notice>

      {!blocked && (
        <div className="mt-6">
          <Button asChild size="lg" className="w-full">
            <Link to={signInTo}>Send me a new link</Link>
          </Button>
          <p className="mt-4 text-center text-[0.8125rem] leading-relaxed text-ink-2">
            {code === "link_used"
              ? "If it was you who used it, you may already be signed in in another tab."
              : "Links are good for one sign-in, and they expire so that an old email in your inbox is not a way into your account."}
          </p>
        </div>
      )}
    </div>
  );
}
